/**
 * generateClientTells — produces 1-2 contextual "tells" (visual hints)
 * to display on the argumentation mini-game so the player can deduce
 * the client's `primaryDriver` from their profile and history.
 *
 * 03/06/2026 — Phase 3 bis enrichment per user request : transform the
 * mini-game from pure deduction/luck into a true "commercial reading"
 * exercise.
 *
 * Each tell is a {Icon, label, hint} triple — the `hint` is a subtle
 * narrative cue (NOT the direct answer). Tells are stable per
 * (clientId, instanceId) pair.
 *
 * @param {Object} client   - RECURRENT_CLIENTS entry (or null for anonymous)
 * @param {Object} contract - the contract being negotiated
 * @param {Object} loyaltyData - state.clientLoyalty[clientId] (events/score)
 * @returns {Array<{key:string, label:string, hint:string, weight:'primary'|'secondary'}>}
 */
import {
  Award, Timer, Euro, MapPin, Building2, Snowflake, Truck, ShoppingBag,
  ShieldAlert, Sparkles, TrendingDown, Eye,
} from 'lucide-react';

const SEGMENT_HINTS = {
  PUB: {
    label: 'Collectivité publique',
    hint: 'Soumise au code des marchés publics. Cahier des charges strict, primauté de la qualité documentée.',
    iconKey: 'building',
    weight: 'primary',
  },
  GC: {
    label: 'Grand compte national',
    hint: 'Volumes importants à tenir, KPIs trimestriels. Tout retard fait remonter les statistiques au siège.',
    iconKey: 'timer',
    weight: 'primary',
  },
  PME: {
    label: 'PME régionale',
    hint: 'Marges serrées, dirigeant attentif au moindre euro. Devis comparés.',
    iconKey: 'euro',
    weight: 'primary',
  },
};

const CATEGORY_HINTS = {
  'Frigorifique': {
    label: 'Chaîne du froid critique',
    hint: 'Rupture de température = lot perdu. Process qualité documenté primordial.',
    iconKey: 'snowflake',
    weight: 'primary',
  },
  'Spécialisé': {
    label: 'Marchandise sensible (ADR / matières dangereuses)',
    hint: 'Réglementation lourde, traçabilité totale. Le moindre incident est rapporté à la DREAL.',
    iconKey: 'shield',
    weight: 'primary',
  },
  'BTP / Matériaux': {
    label: 'Chantier en cours',
    hint: 'Marges minces, devis comparés au centime près. Le moins-disant l\'emporte souvent.',
    iconKey: 'euro',
    weight: 'primary',
  },
  'Export EU': {
    label: 'Flux cross-border programmé',
    hint: 'Fenêtre de livraison serrée, douanes EU à passer. Le retard coûte plus cher que le prix.',
    iconKey: 'timer',
    weight: 'primary',
  },
  'Export Extra-UE': {
    label: 'Logistique internationale',
    hint: 'Conteneurs maritimes ou rail. Délais incompressibles, planning hub-and-spoke.',
    iconKey: 'timer',
    weight: 'primary',
  },
  'RSE / Marchés publics': {
    label: 'Critères RSE et reporting',
    hint: 'Audit ISO 14001 fréquent. Documentation environnementale et sociale exigée.',
    iconKey: 'award',
    weight: 'primary',
  },
  'Vrac / Palettes': {
    label: 'Logistique de volume',
    hint: 'Cycle économique tendu, négociation à la commodité. Tarif compétitif déterminant.',
    iconKey: 'euro',
    weight: 'secondary',
  },
  'Messagerie / Colis': {
    label: 'Tournées récurrentes',
    hint: 'Engagement de service J+1 ou J+2 strict. Manquer un créneau = pénalité contractuelle.',
    iconKey: 'timer',
    weight: 'primary',
  },
};

const LOYALTY_HINTS = {
  partner: {
    label: 'Client partenaire historique',
    hint: 'La relation est solide. Le client valorise la continuité de service plus que le prix.',
    iconKey: 'sparkles',
    weight: 'secondary',
  },
  loyal: {
    label: 'Client fidèle',
    hint: 'Habitué à votre niveau de service. Les écarts qualité sont remarqués immédiatement.',
    iconKey: 'sparkles',
    weight: 'secondary',
  },
  wary: {
    label: 'Client en méfiance',
    hint: 'Vous reprenez une relation tendue. La fiabilité prime sur tout le reste.',
    iconKey: 'shield',
    weight: 'primary',
  },
};

const ICON_MAP = {
  award: Award,
  timer: Timer,
  euro: Euro,
  map: MapPin,
  building: Building2,
  snowflake: Snowflake,
  truck: Truck,
  shop: ShoppingBag,
  shield: ShieldAlert,
  sparkles: Sparkles,
  trending_down: TrendingDown,
  eye: Eye,
};

/**
 * Stable deterministic pseudo-shuffle.
 */
function _seedShuffle(arr, seed) {
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) | 0;
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    h = (h * 1103515245 + 12345) | 0;
    const j = Math.abs(h) % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function generateClientTells(client, contract, loyaltyData) {
  const tells = [];

  // 1. Category tell — strongest signal, always shown when known.
  const catKey = contract?.category;
  if (catKey && CATEGORY_HINTS[catKey]) {
    tells.push({ key: `cat-${catKey}`, ...CATEGORY_HINTS[catKey] });
  }

  // 2. Segment tell — only when client is known. Skip if redundant with category.
  if (client?.segment && SEGMENT_HINTS[client.segment]) {
    // Avoid duplicating the same "primary" weight angle.
    tells.push({ key: `seg-${client.segment}`, ...SEGMENT_HINTS[client.segment] });
  }

  // 3. Loyalty tell — adds nuance based on the relationship history.
  if (loyaltyData?.score !== undefined) {
    const s = loyaltyData.score;
    let key = null;
    if (s >= 80) key = 'partner';
    else if (s >= 60) key = 'loyal';
    else if (s < 20) key = 'wary';
    if (key) tells.push({ key: `loyalty-${key}`, ...LOYALTY_HINTS[key] });
  }

  // Stable shuffle to mix the order (so the first tell isn't always category).
  const shuffled = contract?.instanceId
    ? _seedShuffle(tells, contract.instanceId)
    : tells;

  // Cap at 2 tells to avoid overwhelming the player.
  return shuffled.slice(0, 2).map(t => ({ ...t, Icon: ICON_MAP[t.iconKey] || Eye }));
}
